import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';

export interface StepItem {
  label: string;
  description?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-stepper',
  templateUrl: './app-stepper.component.html',
  styleUrls: ['./app-stepper.component.scss'],
})
export class AppStepperComponent implements OnInit, OnChanges {
  @Input() steps: (string | StepItem)[] = [];
  @Input() activeStep: number = 0;
  @Input() completedSteps: Record<number, boolean> | boolean[] = {};
  @Input() activeColor: string = '#00ba5c';
  @Input() nonLinear: boolean = true;
  @Input() customClass: string = '';
  @Input() width: string = '';
  @Input() maxWidth: string = '540px';
  @Input() align: 'center' | 'left' | 'right' = 'center';

  @Input() isStepValid?: (stepIndex: number) => boolean;

  @Output() stepChange = new EventEmitter<number>();
  @Output() stepClick = new EventEmitter<number>();

  normalizedSteps: StepItem[] = [];

  ngOnInit(): void {
    this.updateNormalizedSteps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['steps']) {
      this.updateNormalizedSteps();
    }
  }

  trackByStep(index: number, step: StepItem): any {
    return step.label || index;
  }

  private updateNormalizedSteps(): void {
    if (!this.steps || !Array.isArray(this.steps)) {
      this.normalizedSteps = [];
      return;
    }
    this.normalizedSteps = this.steps.map((step) => {
      if (typeof step === 'string') {
        return { label: step };
      }
      return step;
    });
  }

  isStepCompleted(index: number): boolean {
    if (this.completedSteps && typeof this.completedSteps === 'object') {
      if (Array.isArray(this.completedSteps)) {
        if (this.completedSteps[index] !== undefined) return Boolean(this.completedSteps[index]);
      } else if (this.completedSteps[index] !== undefined) {
        return Boolean(this.completedSteps[index]);
      }
    }

    // A step is only considered completed if we have progressed past it (index < activeStep)
    if (index < this.activeStep) {
      if (this.isStepValid) {
        return this.isStepValid(index);
      }
      return true;
    }
    return false;
  }

  isStepActive(index: number): boolean {
    return this.activeStep === index;
  }

  isStepHighlighted(index: number): boolean {
    return this.isStepActive(index) || this.isStepCompleted(index);
  }

  isConnectorCompleted(stepIndex: number): boolean {
    return this.isStepCompleted(stepIndex) || this.activeStep > stepIndex;
  }

  canNavigateTo(index: number): boolean {
    if (index <= this.activeStep) return true;
    if (this.isStepValid) {
      for (let i = 0; i < index; i++) {
        if (!this.isStepValid(i)) return false;
      }
    }
    return true;
  }

  onStepSelect(index: number): void {
    const step = this.normalizedSteps[index];
    if (step && step.disabled) return;

    // Prevent jumping forward if previous steps are not valid
    if (index > this.activeStep && !this.canNavigateTo(index)) {
      return;
    }

    this.stepClick.emit(index);
    this.activeStep = index;
    this.stepChange.emit(index);
  }
}
